var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
const loadedAvatarSrcs = new Set();
const avatarPreloadPromises = new Map();
function preloadAvatarSrc(src) {
    if (loadedAvatarSrcs.has(src)) {
        return Promise.resolve();
    }
    const existing = avatarPreloadPromises.get(src);
    if (existing) {
        return existing;
    }
    const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            loadedAvatarSrcs.add(src);
            avatarPreloadPromises.delete(src);
            resolve();
        };
        img.onerror = () => {
            avatarPreloadPromises.delete(src);
            reject(new Error(`Failed to preload avatar: ${src}`));
        };
        img.src = src;
    });
    avatarPreloadPromises.set(src, promise);
    return promise;
}
const Avatar = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(AvatarPrimitive.Root, Object.assign({ ref: ref, className: cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className) }, props)));
});
Avatar.displayName = AvatarPrimitive.Root.displayName;
const AvatarImage = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(AvatarPrimitive.Image, Object.assign({ ref: ref, className: cn("aspect-square h-full w-full object-cover", className) }, props)));
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
const AvatarFallback = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(AvatarPrimitive.Fallback, Object.assign({ ref: ref, className: cn("flex h-full w-full items-center justify-center rounded-full bg-background-neutral-3 text-text-primary font-semibold", className) }, props)));
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
const AVATAR_SKELETON_DELAY_MS = 120;
function AvatarImageWithFallback({ src, alt, fallback, imageClassName, fallbackClassName, skeletonClassName, showLoadingSkeleton = true, }) {
    const normalizedSrc = typeof src === "string" && src.trim() !== "" ? src : undefined;
    const initialStatus = normalizedSrc
        ? loadedAvatarSrcs.has(normalizedSrc)
            ? "loaded"
            : "loading"
        : "error";
    const [status, setStatus] = React.useState(initialStatus);
    const [showSkeleton, setShowSkeleton] = React.useState(false);
    React.useEffect(() => {
        if (!normalizedSrc) {
            setStatus("error");
            return;
        }
        if (loadedAvatarSrcs.has(normalizedSrc)) {
            setStatus("loaded");
            return;
        }
        let cancelled = false;
        setStatus("loading");
        preloadAvatarSrc(normalizedSrc)
            .then(() => {
            if (!cancelled) {
                setStatus("loaded");
            }
        })
            .catch(() => {
            if (!cancelled) {
                setStatus("error");
            }
        });
        return () => {
            cancelled = true;
        };
    }, [normalizedSrc]);
    const handleLoadingStatusChange = React.useCallback((nextStatus) => {
        if (nextStatus === "loading" &&
            normalizedSrc &&
            loadedAvatarSrcs.has(normalizedSrc)) {
            return;
        }
        if (nextStatus === "loaded" && normalizedSrc) {
            loadedAvatarSrcs.add(normalizedSrc);
        }
        setStatus(nextStatus);
    }, [normalizedSrc]);
    React.useEffect(() => {
        if (status !== "loading") {
            setShowSkeleton(false);
            return;
        }
        const timer = window.setTimeout(() => {
            setShowSkeleton(true);
        }, AVATAR_SKELETON_DELAY_MS);
        return () => {
            window.clearTimeout(timer);
        };
    }, [status]);
    if (!normalizedSrc) {
        return (_jsx(AvatarFallback, { className: fallbackClassName, children: fallback }));
    }
    return (_jsxs(_Fragment, { children: [showLoadingSkeleton && status === "loading" && showSkeleton && (_jsx(Skeleton, { className: cn("absolute inset-0 h-full w-full rounded-full", skeletonClassName) })), _jsx(AvatarPrimitive.Image, { src: normalizedSrc, alt: alt, className: cn("aspect-square h-full w-full object-cover", status === "loaded" ? "opacity-100" : "opacity-0", imageClassName), onLoadingStatusChange: handleLoadingStatusChange }), status === "error" && (_jsx(AvatarFallback, { className: fallbackClassName, children: fallback }))] }));
}
export { Avatar, AvatarImage, AvatarFallback, AvatarImageWithFallback };
